import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useTranslation } from '@kgc/i18n';
import { useEffect, useRef, type ReactNode } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { PanelId } from '../RentalWizardPage';
import {
  CostBearerStep,
  DetailsStep,
  EquipmentDetailEditorStep,
  EquipmentPackageStepV2,
  EquipmentStep,
  PackageAndAccessoriesStep,
  PartnerStep,
  SummaryStep,
} from '../steps';
import { STEP_PANELS, useWizardStore } from '../wizard-store';
import { StepNavBar } from './StepNavBar';
import { StickySummaryFooter } from './StickySummaryFooter';
import { WizardTopBar } from './WizardTopBar';

/**
 * StepWizardShell — ADR-103 Amendment-4 (M1.0)
 *
 * A `/rental/new` bérlés-wizard step-by-step megjelenítő rétege a V3-flag
 * (`RENTAL_STEP_WIZARD`) ágán. NÉZET-SZEGMENTÁLÓ az aggregate-state FÖLÖTT
 * (D-4.3): egyszerre 1 lépés látszik, a navigáció SOFT (bármikor bárhová), a
 * HARD aggregate-gate kizárólag a véglegesítésen (`isSubmittable`, a rail-ben).
 *
 * Felelősség:
 *  - a `STEP_PANELS[activeStep]` paneljeinek render-szegmentálása (a meglévő,
 *    külön-tesztelt step-komponensekből — NEM dup-logika)
 *  - breadcrumb step-nav (`gotoStep` jump + `stepsDone` pipa)
 *  - rail-ownership (EGY summary-rail, nincs dupla a page-leftover-rel)
 *  - step-váltási fókusz-management (a11y-regresszió-megelőzés)
 *
 * Az `openPanels:Set` komplementer marad — itt nem használjuk (a step-szegmens
 * mindig „nyitott"); a `getPanelValidity` belőle független.
 */

const STEP_COUNT = 4;
type StepNo = 1 | 2 | 3 | 4;
const STEP_NOS: StepNo[] = [1, 2, 3, 4];

interface StepWizardShellProps {
  /** A véglegesítés handler-e (a meglévő `handleComplete` a RentalWizardPage-ből). */
  onSubmit: () => void;
  isSubmitting: boolean;
  /** HARD aggregate-gate (isSubmittable + override) — csak a véglegesítésre hat. */
  canSubmit: boolean;
}

export function StepWizardShell({ onSubmit, isSubmitting, canSubmit }: StepWizardShellProps) {
  const { t } = useTranslation();
  const isV2 = useFeatureFlag('RENTAL_WIZARD_V2_LAYOUT');
  const isMultiPackage = useFeatureFlag('BERLES_MULTI_PACKAGE');
  // Step-2 gép-detail-editor paradigma (Slice-1 váz). Flag-ON esetén az
  // `equipment` panel ágában az `EquipmentPackageStepV2` helyett az
  // `EquipmentDetailEditorStep` renderelődik. Flag-OFF: 0 változás a V2 ágban.
  const isDetailEditor = useFeatureFlag('RENTAL_STEP2_DETAIL_EDITOR');

  const { activeStep, gotoStep, stepDone } = useWizardStore(
    useShallow(s => ({
      activeStep: s.activeStep,
      gotoStep: s.gotoStep,
      stepDone: s.stepDone,
    }))
  );

  // Fókusz-management (M1.0 acceptance F4): step-VÁLTÁSKOR az új szegmens
  // konténerére visszük a fókuszt, hogy a billentyűzet-/képernyőolvasó-fókusz
  // ne ragadjon a breadcrumb-gombon (a11y-regresszió a heading-fix után).
  // ⚠️ A mount-on NEM fókuszálunk (didMountRef-guard) — különben a szegmensre
  // auto-fókusz belépéskor átugratná a breadcrumb-ot a screen-reader-nek.
  const segmentRef = useRef<HTMLDivElement>(null);
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    segmentRef.current?.focus();
  }, [activeStep]);

  // ── Panel-id → komponens (a V2/multi-package flag-ágakat tükrözve) ─────────
  const renderPanel = (id: PanelId): ReactNode => {
    switch (id) {
      case 'partner':
        return <PartnerStep key="partner" />;
      case 'costBearer':
        return <CostBearerStep key="costBearer" />;
      case 'equipment':
        // `RENTAL_STEP2_DETAIL_EDITOR` flag-ON → EquipmentDetailEditorStep (Slice-1 váz).
        // `RENTAL_WIZARD_V2_LAYOUT` flag-ON (és detail-editor OFF) → EquipmentPackageStepV2.
        // Mindkét flag OFF → EquipmentStep (legacy accordion ág).
        if (isDetailEditor) return <EquipmentDetailEditorStep key="equipment" />;
        return isV2 ? (
          <EquipmentPackageStepV2 key="equipment" />
        ) : (
          <EquipmentStep key="equipment" />
        );
      case 'packages':
        // V2-ben az equipment-step olvasztja → ne dup-renderelj. Legacy +
        // multi-package flag ON esetén külön panel.
        return !isV2 && isMultiPackage ? <PackageAndAccessoriesStep key="packages" /> : null;
      case 'schedule':
        return <DetailsStep key="schedule" />;
      case 'payment':
        return <SummaryStep key="payment" />;
      default: {
        // Exhaustiveness-guard: ha bővül a PanelId, a tsc itt hibázik (never),
        // így új panel nem maradhat árván (sem render, sem stepDone-mapping).
        const _exhaustive: never = id;
        return _exhaustive;
      }
    }
  };

  // ⚠️ A `t(key, params?)` 2. argja a params (interpoláció), NEM fallback-string
  // (ADR-071: `t('key', { count })`). A kulcsok mind a 4 locale-ban léteznek →
  // nincs fallback-string-arg. dev4-smoke-fogás: a korábbi `t(key, '{n}. lépés', {n})`
  // 3-arg hívásnál a {n} nem interpolált (a 2. arg string-ként params-ra esett).
  const stepTitle = (n: StepNo): string => {
    switch (n) {
      case 1:
        return t('rental.stepWizard.step1');
      case 2:
        return t('rental.stepWizard.step2');
      case 3:
        return t('rental.stepWizard.step3');
      case 4:
        return t('rental.stepWizard.step4');
    }
  };

  const breadcrumb = (
    <nav
      aria-label={t('rental.stepWizard.navLabel')}
      className="flex items-center gap-1 overflow-x-auto px-4 py-3 sm:gap-2 sm:px-6 lg:px-8"
    >
      {STEP_NOS.map((n, idx) => {
        const done = stepDone(n);
        const current = n === activeStep;
        return (
          <div key={n} className="flex shrink-0 items-center gap-1 sm:gap-2">
            <button
              type="button"
              onClick={() => gotoStep(n)}
              data-done={done}
              aria-current={current ? 'step' : undefined}
              // a11y (Zeno #215-mellék): a vizuális sorszámot az aria-hidden
              // kör-pötty adja → a screen-reader-nek explicit „N. lépés: cím"
              // (+ kész-állapot), különben csak a címet hallaná. A {n} a t() params
              // 2. argjában (NEM 3-arg fallback — dev4-smoke fogta az interpoláció-bugot).
              aria-label={`${t('rental.stepWizard.stepAria', { n })}: ${stepTitle(n)}${
                done ? ` — ${t('rental.stepWizard.stepDoneAria')}` : ''
              }`}
              className={[
                'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors sm:text-sm',
                current
                  ? 'bg-[var(--accent-soft,#e6f4f1)] text-[var(--accent,#0f766e)]'
                  : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover,#f1f5f9)]',
              ].join(' ')}
            >
              <span
                aria-hidden="true"
                className={[
                  'inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px]',
                  done
                    ? 'bg-[var(--accent,#0f766e)] text-white'
                    : current
                      ? 'border border-[var(--accent,#0f766e)] text-[var(--accent,#0f766e)]'
                      : 'border border-[var(--border-secondary,#cbd5e1)]',
                ].join(' ')}
              >
                {done ? '✓' : n}
              </span>
              {/* A lépésszámot a kör-pötty adja (fent) — a szövegtörzs CSAK a cím,
                  különben dupla szám renderel ("11. Partner") — dev4-smoke-fogás. */}
              {stepTitle(n)}
            </button>
            {idx < STEP_COUNT - 1 && (
              <span aria-hidden="true" className="text-[var(--text-tertiary)]">
                ›
              </span>
            )}
          </div>
        );
      })}
    </nav>
  );

  const activePanels = STEP_PANELS[activeStep];

  return (
    <div className="flex h-full flex-col">
      {/* v3-parity G1+G3+G4: perzisztens felső chrome (MOST FIZETENDŐ + nyelvváltó + bérlés-ID) */}
      <WizardTopBar />
      <div className="shrink-0 border-b border-[var(--border-secondary,#e2e8f0)]">{breadcrumb}</div>
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-6 sm:px-6 lg:flex-row lg:px-8">
          {/* Step-szegmens — a fókusz-management célpontja (tabindex=-1) */}
          <main
            ref={segmentRef}
            tabIndex={-1}
            data-testid="step-segment"
            className="min-w-0 flex-1 space-y-4 outline-none"
          >
            {activePanels.map(id => renderPanel(id))}
            {/* M1.1: in-flow léptetés-nav (Tovább/Vissza, SOFT) — a szegmens
                végén, NEM a rail-ben. A rail tiszta ár + step4-Kiadás marad. */}
            <StepNavBar />
          </main>
          {/* Rail-ownership: EGY summary-rail a shellben (nincs dupla a page-leftover-rel) */}
          <aside className="w-full shrink-0 lg:w-[380px]" data-testid="rental-step-summary-rail">
            <div className="lg:sticky lg:top-24">
              <StickySummaryFooter
                variant="rail"
                onSubmit={onSubmit}
                isSubmitting={isSubmitting}
                canSubmit={canSubmit}
              />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
